import { useMemo } from 'react'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useQuery } from '@percent/utility'

const NOMINATOR_GROUP_QUESTION_KEY = 'nomination.nominator.group'

type NominatorGroupOption = { label: string; value: string }

export const useNominatorGroupOptions = ({
  programId,
  programName: externalProgramName,
  showProgramName = false,
  enabled = true
}: {
  programId: string | undefined
  programName?: string | undefined
  showProgramName?: boolean
  enabled?: boolean
}) => {
  const { grantProgramService } = useServices()

  const programQuery = useQuery(
    ['program-for-nominator-groups', programId],
    grantProgramService.getProgramById,
    { programId: programId || '' },
    { enabled: enabled && !!programId }
  )

  const program = programQuery.data
  const nominationFormId = program?.nominationFormId
  const programName = externalProgramName ?? program?.name

  const formQuery = useQuery(
    ['nomination-form-for-nominator-groups', nominationFormId],
    grantProgramService.getFormById,
    { formId: nominationFormId || '' },
    { enabled: enabled && !!nominationFormId }
  )

  const formDetails = formQuery.data

  const nominatorGroupOptions: NominatorGroupOption[] = useMemo(() => {
    if (!formDetails?.pages) return []

    for (const page of formDetails.pages) {
      for (const question of page.questions) {
        if (question.key === NOMINATOR_GROUP_QUESTION_KEY && question.options) {
          return question.options.map(opt => ({
            label: showProgramName && programName ? `${opt.label} - ${programName}` : opt.label,
            value: opt.value
          }))
        }
      }
    }

    return []
  }, [formDetails, programName, showProgramName])

  return {
    nominatorGroupOptions,
    isLoading: programQuery.isLoading || formQuery.isLoading,
    hasNominationForm: !!nominationFormId
  }
}
